import {
  NodoDto,
  useGetRegistriDispositivo,
  useUpdateRegistriDispositivo,
} from "@/api";
import { useAuth } from "@/context/useAuth";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import { withOpeningReset } from "@/utils/withOpeningReset";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { isNil, cloneDeep } from "lodash";
import { toast } from "react-toastify";
import { SmallLoading } from "../Loading";
import { getMascheraDispositivoHelper } from "@/components/DispositivoDetails/maschere";

function RegistriMqttDialog({
  open = false,
  onClose,
  dispositivo,
}: {
  open?: boolean;
  onClose?: () => void;
  dispositivo?: NodoDto;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();

  const _helper = dispositivo?.dispositivoModello?.maschera
    ? getMascheraDispositivoHelper(dispositivo.dispositivoModello.maschera)
    : null;
  const helper = _helper && "registriToForm" in _helper ? _helper : null;

  const { data } = useGetRegistriDispositivo(dispositivo?.id ?? 0, {
    query: { enabled: Boolean(open && dispositivo?.id && helper) },
  });

  const {
    control,
    reset,
    resetField,
    handleSubmit,
    formState: { errors },
    register,
    setError,
    clearErrors,
    getValues,
  } = useForm<any>({});
  const [initialData, setInitialData] = useState<any>({});

  const { mutateAsync, isLoading } = useUpdateRegistriDispositivo();

  useEffect(() => {
    if (!data) {
      return;
    }

    if (helper) {
      const formData = helper.registriToForm(data.data);
      reset(formData);
      setInitialData(cloneDeep(formData));
    }
  }, [data, reset, helper]);

  const handleClose = () => {
    onClose?.();
  };

  async function onSubmit(form: any) {
    if (!dispositivo || !helper || !data?.data) {
      return;
    }

    clearErrors();

    const newValues = helper.formToRegistri(form);
    const differences = [];

    for (const newValue of newValues) {
      if (
        !isNil(newValue) &&
        initialData[newValue.registro] !== form[newValue.registro]
      ) {
        differences.push(newValue);
      }
    }

    if (differences.length > 0) {
      try {
        console.log("differences", differences);

        const response = await mutateAsync({
          id: dispositivo.id,
          data: differences,
          mode: helper.mode,
        });

        if (!response.data) {
          toast.error(t("registriMqttDialog.errori.modificaRegistri"));
          return;
        }

        let nonAggiornati = 0;
        let aggiornatiDiversi = 0;
        let aggiornatiCorrettamente = 0;

        for (const update of response.data) {
          const registro = update.registro;

          if (
            update.changed &&
            update.newValue !== null &&
            update.newValue !== undefined &&
            update.newValueTime !== null &&
            update.newValueTime !== undefined
          ) {
            const registroAggiornato = {
              registro: registro,
              value: update.newValue,
              time: update.newValueTime,
            };
            const mapped = helper.registriToForm([registroAggiornato]);

            // mostro il valore più aggiornato fornito dal dispositivo
            resetField(registro, {
              defaultValue: mapped[registro],
            });

            if (mapped[registro] === form[registro]) {
              aggiornatiCorrettamente++;
            } else {
              aggiornatiDiversi++;
              setError(registro, {
                message: t("registriMqttDialog.errori.valoreDiverso"),
              });
            }
          } else {
            nonAggiornati++;
            setError(registro, {
              message: t("registriMqttDialog.errori.confermaNonRicevuta"),
            });
          }
        }

        console.log("nonAggiornati", nonAggiornati);
        console.log("aggiornatiDiversi", aggiornatiDiversi);
        console.log("aggiornatiCorrettamente", aggiornatiCorrettamente);

        if (differences.length === aggiornatiCorrettamente) {
          toast.success(t("registriMqttDialog.toast.salvati"));
          handleClose();
        } else if (differences.length === nonAggiornati) {
          toast.error(t("registriMqttDialog.toast.nessunaRisposta"));
        } else {
          toast.warn(t("registriMqttDialog.toast.parziale"));
        }
      } catch (e) {
        console.error(e);
        toast.error(t("registriMqttDialog.toast.erroreSalvataggio"));
      }
    } else {
      handleClose();
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
      <DialogTitle>{t("registriMqttDialog.titolo")}</DialogTitle>
      <DialogContent>
        {helper ? (
          <helper.MascheraComponent
            control={control}
            errors={errors}
            register={register}
            readOnly={isLoading || !hasPermission("DispositivoRegistriWrite")}
            getValues={getValues}
          />
        ) : (
          <Box justifyContent="center" alignItems="center" padding={2}>
            <Typography>
              {t("registriMqttDialog.messaggi.nonSupportato")}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {isLoading && <SmallLoading sx={{ mr: 1 }} />}
        {hasPermission("DispositivoRegistriWrite") && (
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoading}
            onClick={handleSubmit(onSubmit)}
          >
            {t("azioni.salva")}
          </Button>
        )}
        <Button
          variant="contained"
          color="light"
          disabled={isLoading}
          onClick={handleClose}
        >
          {t("azioni.annulla")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withOpeningReset(RegistriMqttDialog);
